/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Avatar, Box, Card, CardContent, Grid2, Tooltip, Typography } from "@mui/material";
import SearchBox from "../../components/form/SearchBox/SearchBox";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import { AUDITRACEACTIONS, dataGridHeaderHeight, dataGridRowHeight, GLOBALSETTINGS, SEARCHBOX, SEVERITIES } from "../../data/constants";
import { DataGridPro, GridActionsCellItem, GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { useUser } from "../../context/UserContext";
import { useCallback, useEffect, useState } from "react";
import RestoreIcon from '@mui/icons-material/Restore';
import { GetAllFilesAndFoldersByUserDto } from "../../types/FolderPermission";
import useApi from "../../hooks/useApi";
import { FileRecycleRequestDto, GetAllDeletedItemsRequestDto, RecycleRequestDto } from "../../types/Recycle";
import { fileRestore, getAllDeletedItemsByUser, getExistingFile, getExistingFolderFiles, restore } from "../../services/recycleService";
import FileReplacer from "./FileReplacer";
import { useToaster } from "../../hooks/useToaster";
import { GLOBALMESSAGES, RECYCLEMESSAGES } from "../../constants/messages";
import { FolderFileType } from "../../data/Enums";
import "./recycle.scss";
import { filterDataBySearch, getFileExtension, getInitials, renderSvgIcon } from "../../utils/commonHelper";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { getCurrentESTTime } from "../../utils/dateHelper";
import { deepOrange } from "@mui/material/colors";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import { useSearchParams } from "react-router-dom";
const Recycle: React.FC = () => {
  const [openFileReplacer, setOpenFileReplacer] = useState(false);
  const [currentRow, setCurrentRow] = useState<GetAllFilesAndFoldersByUserDto | null>(null);
  const [existingFileCount, setExistingFileCount] = useState<number>(0);
  const [fileName, setFileName] = useState<string>("");
  const { userId } = useUser();
  const [searchResult, setSearchResult] = useState('');
  const { loading, executeApi } = useApi();
  const showToaster = useToaster();
  const [allData, setAllData] = useState<GetAllFilesAndFoldersByUserDto[]>([]);
  const apiRef = useGridApiRef();
  const [searchParams] = useSearchParams();
  let folderIdFromURL = parseInt(searchParams.get("folderId"), 10);
  const fileIdFromURL = parseInt(searchParams.get("fileId"), 10);

  const [pageState, setPageState] = useState<{ data: GetAllFilesAndFoldersByUserDto[] | null; total: number }>({
    data: null,
    total: 0,
  });

  const fetchAllDeletedFilesFolders = useCallback(async () => {
    if (userId !== null) {
      const requestData: GetAllDeletedItemsRequestDto = {
        userId
      };

      const allDeletedFilesFolders = await executeApi<GetAllDeletedItemsRequestDto, GetAllFilesAndFoldersByUserDto[]>(
        getAllDeletedItemsByUser,
        requestData
      );
      setAllData(allDeletedFilesFolders ?? []);
      setPageState({
        data: allDeletedFilesFolders,
        total: allDeletedFilesFolders && allDeletedFilesFolders?.length > 0 ? allDeletedFilesFolders[0].totalRecordCount : 0,
      });
    }
  }, [userId, executeApi]);

  useEffect(() => {
    fetchAllDeletedFilesFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAllDeletedFilesFolders]);

  const scrollToFilesSection = () => {
    const targetElement = document.getElementById("recycle-list");
    if (targetElement) {
      const top = targetElement.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({
        top: top,
        behavior: "smooth",
      });
    }
  };


  useEffect(() => {
    if (!apiRef?.current) return;
    if (!pageState?.data) return;
    if (!searchParams.get("fileId") && !searchParams.get("folderId") &&
      !searchParams.get("topLevelId")) return;
    if (window.location.pathname !== GLOBALSETTINGS.RECYCLEBIN_PAGE) {
      return;
    }

    const rowToScrollTo = pageState?.data?.find(
      (row) => row.fileId === fileIdFromURL || row.folderId === folderIdFromURL ||
        row.folderId === parseInt(searchParams.get("topLevelId"), 10)
    );

    if (rowToScrollTo) {
      scrollToFilesSection();

      const rowIndex = pageState.data.indexOf(rowToScrollTo);

      setTimeout(() => {
        apiRef?.current?.scrollToIndexes?.({ rowIndex });
        apiRef?.current?.setRowSelectionModel([rowToScrollTo.roleFolderPermissionsId]);
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }, 150);
    }
  }, [pageState.data, searchParams]);


  useEffect(() => {
    if (searchResult) {
      const lowerText = searchResult.toLowerCase();
      const filtered = filterDataBySearch(allData, lowerText);

      setPageState({
        data: filtered ?? [],
        total: filtered?.length,
      });
    }
    else {
      setPageState({
        data: allData ?? [],
        total: allData?.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult]);

  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
  }, []);

  const handleRestore = async (row: GetAllFilesAndFoldersByUserDto) => {
    if (row.type === FolderFileType[FolderFileType.Folder]) {
      await handleFolderRestore(row);
    }
    else {
      await handleFileRestore(row);
    }
  }

  const handleFolderRestore = async (row: GetAllFilesAndFoldersByUserDto) => {
    const requestData: RecycleRequestDto = {
      userId: userId,
      folderId: row.folderId,
      isSkip: false
    };

    const existingFolderFiles = await executeApi<RecycleRequestDto, string[]>(
      getExistingFolderFiles,
      requestData
    );
    let fileCount = 0;
    if (existingFolderFiles && existingFolderFiles.length > 0) {
      fileCount = existingFolderFiles.filter((file) => {
        const lastDotIndex = file.lastIndexOf(".");
        return lastDotIndex > 0 && lastDotIndex < file.length - 1;
      }).length;
    }


    if (fileCount > 0) {
      if (fileCount === 1) {
        const fileNameWithExt = existingFolderFiles.find((file) => {
          const lastDotIndex = file.lastIndexOf(".");
          return lastDotIndex > 0 && lastDotIndex < file.length - 1;
        });
        setFileName(fileNameWithExt);
      }
      setCurrentRow(row);
      setOpenFileReplacer(true);
      setExistingFileCount(fileCount);
    }
    else {
      restoreOrSkip(false, row);
    }
  };

  const handleFileRestore = async (row: GetAllFilesAndFoldersByUserDto) => {
    const requestData = getFileRequestData(false, row);
    const existingFile = await executeApi<FileRecycleRequestDto, boolean>(
      getExistingFile,
      requestData
    );
    if (existingFile) {
      setFileName(row.description);
      setCurrentRow(row);
      setOpenFileReplacer(true);
      setExistingFileCount(1);
    }
    else {
      restoreOrSkip(false, row);
    }
  };

  const getFileRequestData = (isSkip: boolean, row: GetAllFilesAndFoldersByUserDto) => {
    const requestData: FileRecycleRequestDto = {
      userId: userId,
      fileId: row.fileId,
      parentFolderId: row.parentFolderId === 0 ? null : row.parentFolderId,
      isSkip: isSkip
    };
    return requestData;
  }

  const restoreOrSkip = async (isSkip: boolean, row: GetAllFilesAndFoldersByUserDto) => {    
    if(isSkip){
      closeFileReplacer();
      return false;
    }
    let result = false;
    if (row.type === FolderFileType[FolderFileType.Folder]) {
      const requestData: RecycleRequestDto = {
        userId: userId,
        folderId: row.folderId,
        isSkip: isSkip
      };
      result = await executeApi<RecycleRequestDto, boolean>(
        restore,
        requestData
      );
    }
    else {
      const requestData = getFileRequestData(isSkip, row);
      result = await executeApi<FileRecycleRequestDto, boolean>(
        fileRestore,
        requestData
      );
    }

    let actionDescription = AUDITRACEACTIONS.RESTORE.toString();
    if (!result) {
      actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
    }

    logAuditTrace({
      userId: userId,
      objectType: row.type,
      objectId: row.type === FolderFileType[FolderFileType.Folder] ? row.folderId : row.fileId,
      objectName: row.description,
      action: actionDescription,
      navigationDateTime: getCurrentESTTime(),
    }, null, true);

    const message = result ? RECYCLEMESSAGES.RESTORE_SUCCESSFUL : RECYCLEMESSAGES.RESTORE_FAILED;
    const severity = result ? SEVERITIES.SUCCESS : SEVERITIES.ERROR;
    showToaster(message, severity);
    closeFileReplacer();
  }

  const closeFileReplacer = async () => {
    setOpenFileReplacer(false)
    await fetchAllDeletedFilesFolders();
  }

  const closeFileReplacerDialog = async () => {
    setOpenFileReplacer(false);
  }

  const columns: GridColDef[] = [
    {
      field: "description",
      headerName: "Name",
      flex: 1,
      minWidth: 350,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}
        >
          {params.row.type === FolderFileType[FolderFileType.File] ? (
            renderSvgIcon(getFileExtension(params.row.description), "file-icon")
          ) : (
            <img src="/folder.svg" alt="Folder Icon" />
          )}
          <Tooltip title={params.row.description} placement="bottom-start">
            <Typography variant="body2" className="grid-ellipsis-text">{params.row.description}</Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 90,
    },
    {
      field: "createdBy",
      headerName: "Deleted By",
      width: 180,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center', // vertical align
            justifyContent: 'flex-start', // horizontal align
            height: '100%', // ensures full cell height
            gap: 1,
          }}
        >
          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: deepOrange[400] }}>
            {getInitials(params.value)}
          </Avatar>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "createdDate",
      headerName: "Deleted On",
      width: 120,
      valueFormatter: (value) => value ? format(value, GLOBALSETTINGS.DATE_FORMAT) : "",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      hideable: false,
      filterable: false,
      disableColumnMenu: false,
      renderCell: (params) => (
        <GridActionsCellItem
          icon={
            <Tooltip title="Restore">
              <RestoreIcon />
            </Tooltip>
          }
          label="Restore"
          onClick={() => handleRestore(params.row)}
        />
      ),
    },
  ];

  const {
    columnState,
    pinnedColumns,
    setPinnedColumns,
    handleColumnResize,
  } = useResizableColumns(columns);

  return (
    <section className='mb-3' id="recycle-list">
      <Card>
        <CardContent>
          <Typography variant="h5" component="div" className="mb-3 card-title">Recycled Folders & File</Typography>
          {<BackdropProgressBar loading={loading} showAbsolute={true} />}
          <Box component="form">
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <SearchBox searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER} onSearch={handleSearchText} />
              </Grid2>
              <Grid2 size={12}>
                <div className="grid-height">
                  <DataGridPro
                    apiRef={apiRef}
                    rows={pageState.data ?? []}
                    columns={columnState}
                    rowCount={pageState.total}
                    columnHeaderHeight={dataGridHeaderHeight}
                    rowHeight={dataGridRowHeight}
                    pageSizeOptions={[]}
                    getRowId={(row) => row.roleFolderPermissionsId}
                    hideFooterPagination
                    hideFooterSelectedRowCount={true}
                    localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                    onColumnWidthChange={(params) => handleColumnResize(params.width, params.colDef)}
                    getRowClassName={(params) => {
                      const topLevelId = parseInt(searchParams.get("topLevelId") ?? "-1", 10);

                      const isHighlighted =
                        params.row.fileId === fileIdFromURL ||
                        params.row.folderId === folderIdFromURL ||
                        params.row.folderId === topLevelId;

                      return isHighlighted ? "highlighted-row" : "";
                    }}

                  />
                </div>
              </Grid2>
              <Grid2>
                <FileReplacer open={openFileReplacer} currentRow={currentRow}
                  onClose={closeFileReplacerDialog} handleRestore={restoreOrSkip}
                  fileCount={existingFileCount} fileName={fileName} />
              </Grid2>
            </Grid2>
          </Box>
        </CardContent>
      </Card>
    </section>
  );
};


export default Recycle;